import React, { useCallback, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  AppModal,
  AppRemixIcon,
  AppInput,
  AppNoInfo,
} from '@/layout/AppComponents';
import { ProgressBar } from 'react-bootstrap';
import Switch from 'react-switch';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Moment from 'moment';
import ReactDOMServer from 'react-dom/server';
import NotificationAlert from '../../../../../../components/helpers/notificationAlert';
import * as currentPageAction from '../../../../../../../store/actions/currentPageAction';
import AuxSidebar from '../../../../../../Sidebar/AuxSidebar';
import { axiosPath } from '../../../../../../../routing/helpers/router';
import Translator from '../../../../../../components/helpers/Translator';
import MilestoneItem from './MilestoneItem';
import {
  getGCLReportIcon,
  getGCLReportIconColor,
} from '../../../../../../../constants/GCL/gclReports';
import useIsMounted from '../../../../../../components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import _ from 'lodash';

const MilestoneSidebarBlock = () => {
  const isMounted = useIsMounted();
  const dispatch = useDispatch();
  const { moduleBridgeHex, plantHex, filtersHex } = useParams();
  const auxSidebar = useSelector(
    (state) => state.currentPage?.auxSidebar?.opened
  );
  const reportPermissions = useSelector(
    (state) =>
      state.module[moduleBridgeHex]?.permissions?.gcl?.module?.plant?.reports
  );
  const permissions = useSelector(
    (state) => state.module[moduleBridgeHex]?.permissions
  );
  const autoSave = useRef();
  const [openOnce, setOpenOnce] = useState(false);
  const [showModal, setShowModal] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [generatedMilestoneValues, setGeneratedMilestoneValues] =
    useState(null);

  const handleAuxSidebar = () => {
    if (!openOnce) setOpenOnce(true);
    if (auxSidebar !== 'milestones') {
      dispatch(
        currentPageAction.change('auxSidebar', { opened: 'milestones' })
      );
    } else dispatch(currentPageAction.change('auxSidebar', { opened: null }));
  };

  // > LOAD MILESTONES
  const loadMilestones = async () => {
    try {
      const response = await axios.post(
        axiosPath("api_r_user_module_legalCompliance_milestone_getMilestones"),
        { moduleBridgeHex, plantHex }
      );
      return response.data;
    } catch (err) {}
  };

  useEffect(() => {
    if (!generatedMilestoneValues) {
      setIsLoading(true);
      loadMilestones().then((r) => {
        if (isMounted) {
          setMilestones(r);
          setIsLoading(false);
        }
      });
    }
  }, [generatedMilestoneValues]);
  // < LOAD MILESTONES

  // > GENERATE MILESTONE
  const generateMilestone = useCallback(
    () =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_legalCompliance_milestone_generateMilestone"
          ),
          {
            moduleBridgeHex,
            plantHex,
            filtersHex,
            values: generatedMilestoneValues,
          }
        )
        .catch((err) => {
          dispatch(errorHandler(err));
        }),
    [generatedMilestoneValues, filtersHex]
  );

  const handleOnSubmit = (values) => {
    const reports = _.keys(_.pickBy(values.reports));
    setGeneratedMilestoneValues({
      ...values,
      reportPercent: parseInt(90 / reports.length),
      currentPercent: 10,
      totalReports: reports.length,
      reports,
    });
  };

  useEffect(() => {
    if (generatedMilestoneValues?.reports?.length > 0) {
      generateMilestone()
        .then((r) => {
          if (isMounted) {
            const updatedGeneratedMilestoneValues = {
              ...generatedMilestoneValues,
            };
            updatedGeneratedMilestoneValues.milestoneID = r.data.milestoneID;
            updatedGeneratedMilestoneValues.reports.shift();
            updatedGeneratedMilestoneValues.currentPercent +=
              updatedGeneratedMilestoneValues.reportPercent;
            setGeneratedMilestoneValues(updatedGeneratedMilestoneValues);
          }
        })
        .catch(() => {
          NotificationAlert({
            title: ReactDOMServer.renderToString(
              <Translator file="user" name="user.error" />
            ),
            message: ReactDOMServer.renderToString(
              <Translator
                file="module-legalCompliance"
                name="error.milestone.incomplete"
              />
            ),
            type: 'danger',
            duration: 0,
          });
          setGeneratedMilestoneValues(null);
          setShowModal(null);
        });
    } else {
      setGeneratedMilestoneValues(null);
      setShowModal(null);
    }
  }, [generatedMilestoneValues]);
  // < GENERATE MILESTONE

  const handleCloseModal = () => {
    if (!generatedMilestoneValues) setShowModal(null);
  };

  const handleSelectMilestone = (
    selectedReports,
    setFieldValue,
    reportType
  ) => {
    const updatedSelectedReports = { ...selectedReports };
    updatedSelectedReports[reportType] = !updatedSelectedReports[reportType];
    setFieldValue('reports', updatedSelectedReports);
  };

  // < FORM SCHEMA
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .min(2, ReactDOMServer.renderToString(<Translator file="user" name="user.min.2.characters" />))
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
  });
  // < FORM SCHEMA

  useEffect(() => {
    if (showModal === 'autoSave') autoSave.current.click();
  }, [showModal]);

  return (
    <>
      <li
        className={auxSidebar && auxSidebar === 'milestones' ? 'active' : ''}
        onClick={handleAuxSidebar}
      >
        <span className="sidebar-element">
          <AppRemixIcon icon="file-history" className="sidebar-icon" />{' '}
          <span className="sb-title">
            <Translator file="module-legalCompliance" name="user.milestone" />
          </span>
        </span>
      </li>
      {permissions.gcl?.module?.plant?.milestoneReportsShortcut && (
        <li onClick={() => setShowModal('autoSave')}>
          <span className="sidebar-element btn-success btn-sm text-white">
            <AppRemixIcon icon="download-2" className="sidebar-icon" />{' '}
            <span className="sb-title">
              <Translator file="module-legalCompliance" name="save.milestone" />
            </span>
          </span>
        </li>
      )}
      <AuxSidebar
        title={
          <Translator file="module-legalCompliance" name="user.milestone" />
        }
        isOpen={auxSidebar && auxSidebar === 'milestones'}
        width="350px"
        position="left"
        onClose={handleAuxSidebar}
      >
        {openOnce && (
          <>
            <span
              className="btn btn-sm btn-info btn-block mb-2"
              onClick={() => setShowModal('manual')}
            >
              <Translator
                file="module-legalCompliance"
                name="generate.new.milestone"
              />
            </span>
            {isLoading ? (
              <AppNoInfo />
            ) : milestones ? (
              milestones.map((m) => (
                <React.Fragment key={m.id}>
                  <MilestoneItem milestone={m} onRemove={setMilestones} />
                </React.Fragment>
              ))
            ) : (
              <div className="alert alert-info">
                <Translator
                  file="module-legalCompliance"
                  name="there.are.no.milestones"
                />
              </div>
            )}
          </>
        )}
      </AuxSidebar>
      <AppModal
        show={showModal !== null}
        title={
          <Translator file="module-legalCompliance" name="new.milestone" />
        }
        onClose={handleCloseModal}
      >
        <Formik
          initialValues={{
            name: showModal === 'autoSave' ? Moment().format('DD/MM/YYYY') : '',
            comment: '',
            reports: Object.fromEntries(
              Object.entries(reportPermissions).map(([key, val]) => [
                key,
                val === 'milestone' || val?.milestone === true,
              ])
            ),
          }}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          enableReinitialize
          onSubmit={handleOnSubmit}
        >
          {({ setFieldValue, handleSubmit, values, errors }) => (
            <>
              {generatedMilestoneValues ? (
                <>
                  {generatedMilestoneValues && (
                    <>
                      <div>
                        <Translator
                          file="module-legalCompliance"
                          name="milestone.ajax.generate.title"
                        />{' '}
                        <b>
                          <Translator
                            file="module-legalCompliance"
                            name={`module.lc.export.${generatedMilestoneValues.reports[0]}`}
                          />
                        </b>
                        ...
                      </div>
                      <ProgressBar
                        now={generatedMilestoneValues.currentPercent}
                        label={`${generatedMilestoneValues.currentPercent}%`}
                        animated
                      />
                    </>
                  )}
                </>
              ) : (
                <>
                  {showModal === 'autoSave' && (
                    <>
                      <AppNoInfo
                        text={
                          <Translator
                            file="module-legalCompliance"
                            name="name"
                          />
                        }
                      />
                      <span ref={autoSave} onClick={() => handleSubmit()} />
                    </>
                  )}
                  {showModal === 'manual' && (
                    <>
                      <div className="row">
                        <div className="col-lg-12">
                          <AppInput
                            title={
                              <Translator
                                file="module-legalCompliance"
                                name="name"
                              />
                            }
                            icon={
                              <AppRemixIcon icon="double-quotes-l" noVariants />
                            }
                            onChange={(opt) => setFieldValue('name', opt)}
                            value={values.name}
                            error={errors.name}
                          />
                        </div>
                        <div className="col-lg-12 mb-2">
                          <AppInput
                            title={
                              <Translator
                                file="module-legalCompliance"
                                name="comment"
                              />
                            }
                            icon={<AppRemixIcon icon="chat-1" />}
                            onChange={(opt) => setFieldValue('comment', opt)}
                            value={values.comment}
                            error={errors.comment}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-lg-12 mb-2">
                          {_.map(reportPermissions, (val, key) => {
                            if (
                              val === 'milestone' ||
                              val?.milestone === true
                            ) {
                              return (
                                <div key={key}>
                                  <label
                                    className="pointer mb-2"
                                    title="Seleccionar informe"
                                  >
                                    <Switch
                                      onChange={() =>
                                        handleSelectMilestone(
                                          values.reports,
                                          setFieldValue,
                                          key
                                        )
                                      }
                                      checked={values.reports[key]}
                                      height={10}
                                      width={25}
                                    />
                                    <AppRemixIcon
                                      icon={getGCLReportIcon(key)}
                                      className="sidebar-icon ml-1 mr-1"
                                      color={getGCLReportIconColor(key)}
                                    />
                                    <Translator
                                      file="module-legalCompliance"
                                      name={`module.lc.export.${key}`}
                                    />
                                  </label>
                                </div>
                              );
                            }
                          })}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-lg-12">
                          <span
                            className="btn btn-success btn-block"
                            onClick={handleSubmit}
                          >
                            <Translator
                              file="module-legalCompliance"
                              name="generate.milestone"
                            />
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Formik>
      </AppModal>
    </>
  );
};
export default MilestoneSidebarBlock;
